import { FC, useState } from 'react';
import { IonButton, IonCardContent, IonContent } from '@ionic/react';
import { Modal } from '../components/Modal';
import { TextField } from '../components/TextField';
import { Text } from '../components/Text';
import { PhoneField, IPhoneContactMethod } from '../contact-methods/PhoneField';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { UserState } from './UserState';
import { useSubscription } from '@meraki-internal/state';
import { MixPanelEventEmitter } from '../metrics/MixPanelEventEmitter';
import { createUseStyles } from 'react-jss';
import { IAvatarPhotoColor } from '../avatar/PhotoAvatarOrColorPicker';
import { MemberAvatar } from '../household/MemberAvatar';
import { EditAvatarDrawer } from '../avatar/EditAvatarDrawer';
import { IDrawerModalAnimationOrigin } from '../components/CareMapDrawerPage';
import { DrawerListSection } from '../components/DrawerListSection';
import { CaremapMemoriesModelFactory } from '../memories/CaremapMemoriesModelFactory';
import { MemoryList } from '../memories/MemoryList';

export type IAccountSettingsModalProps = {
    isOpen?: boolean;
    closeHandler: () => void;
    required?: boolean;
    requireLastName?: boolean;
};

const useStyles = createUseStyles({
    form: {
        display: 'flex',
        flexDirection: 'column',
        gap: 20,
        paddingTop: 8
    },
    readOnlyField: {
        opacity: 0.6,
        pointerEvents: 'none'
    }
});

export const AccountSettingsModal: FC<IAccountSettingsModalProps> = ({
    isOpen = false,
    closeHandler,
    required = false,
    requireLastName = false
}) => {
    const classes = useStyles();

    const userState = useInstance(UserState);
    useSubscription(() => userState);

    const memoriesFactory = useInstance(CaremapMemoriesModelFactory);

    const mixpanel = useInstance(MixPanelEventEmitter);

    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [address, setAddress] = useState('');
    const [mobilePhone, setMobilePhone] = useState<IPhoneContactMethod>({ type: 'mobile-phone', value: '' });
    const [workPhone, setWorkPhone] = useState<IPhoneContactMethod>({ type: 'work-phone', value: '' });
    const [avatar, setAvatar] = useState<IAvatarPhotoColor>({});
    const [isAvatarDrawerOpen, setIsAvatarDrawerOpen] = useState(false);
    const [avatarDrawerAnimationOrigin, setAvatarDrawerAnimationOrigin] = useState<IDrawerModalAnimationOrigin>();
    const avatarClicked = (e: React.MouseEvent) => {
        setAvatarDrawerAnimationOrigin({ x: e.clientX, y: e.clientY });
        setIsAvatarDrawerOpen(true);
    };

    const loadSettings = () => {
        setFirstName(userState.state.firstName || '');
        setLastName(userState.state.lastName || '');
        setAddress(userState.state.address || '');
        setMobilePhone({ type: 'mobile-phone', value: userState.state.phone || '' });
        setWorkPhone({ type: 'work-phone', value: userState.state.workPhone || '' });
        setAvatar({ photo: userState.state.pictureUrl, avatar: userState.state.avatar, color: userState.state.color });
        mixpanel.track('Account Settings Opened');
    };

    const isValid = (() => {
        if (!firstName.trim()) {
            return false;
        }
        if (requireLastName && !lastName.trim()) {
            return false;
        }
        return true;
    })();

    const getChangedFields = (): string[] => {
        const changed: string[] = [];
        if (firstName !== (userState.state.firstName || '')) {
            changed.push('firstName');
        }
        if (lastName !== (userState.state.lastName || '')) {
            changed.push('lastName');
        }
        if (address !== (userState.state.address || '')) {
            changed.push('address');
        }
        if (mobilePhone.value !== (userState.state.phone || '')) {
            changed.push('mobilePhone');
        }
        if (workPhone.value !== (userState.state.workPhone || '')) {
            changed.push('workPhone');
        }
        const avatarChanged = (
            (avatar.photo || '') !== (userState.state.pictureUrl || '') ||
            (avatar.avatar || '') !== (userState.state.avatar || '') ||
            (avatar.color || '') !== (userState.state.color || '')
        );
        if (avatarChanged) {
            changed.push('avatar');
        }
        return changed;
    };

    const onSave = async () => {
        const fieldsChanged = getChangedFields();

        userState.setFirstName(firstName);
        userState.setLastName(lastName);
        userState.setAddress(address);
        if (mobilePhone.value) {
            userState.setPhone(mobilePhone.value.replace(/\D/g, ''));
        }
        // workPhone is canonicalized to "{10 digits}" or "{10 digits}x{extension}",
        // stripping the mask's display formatting (dashes, the space before "x").
        // The trailing-"x" trim handles the case where the user typed the extension
        // marker but no extension digits.
        if (workPhone.value) {
            const normalized = workPhone.value.replace(/[^\dx]/g, '').replace(/x$/, '');
            if (normalized) {
                userState.setWorkPhone(normalized);
            }
        }
        // Set one avatar field and clear the others, mirroring PhotoAvatarOrColorPicker's contract:
        // only one of photo/avatar/color is active at a time.
        userState.setPictureUrl(avatar.photo || '');
        userState.setAvatar(avatar.avatar || '');
        userState.setColor(avatar.color || '');

        await userState.save();

        mixpanel.track('Account Settings Saved', () => ({
            'Fields Changed': fieldsChanged
        }));

        closeHandler();
    };

    return (
        <>
            <Modal
                isOpen={isOpen}
                desktopMaxWidth={535}
                title="Account Settings"
                data-id="account-settings-modal"
                closeHandler={closeHandler}
                onDidPresent={loadSettings}
                hideCloseButton={required}
                preventBackdropDismiss={required}
            >
                <IonContent data-id="account-settings-form">
                    <IonCardContent style={{paddingLeft: 22, paddingRight: 22}}>
                        <div className={classes.form}>
                            <div style={{ display: 'flex', alignItems: 'start', width: '100%', gap: 20 }}>
                                <div data-id="account-avatar">
                                    <MemberAvatar member={{ photo: avatar.photo, color: avatar.color, avatar: avatar.avatar }} onClick={avatarClicked} />
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 20, flexGrow: 1 }}>
                                    <TextField
                                        name="first-name"
                                        label="First Name"
                                        value={firstName}
                                        autocapitalize="words"
                                        required
                                        onInput={setFirstName}
                                    />

                                    <TextField
                                        name="last-name"
                                        label={requireLastName ? 'Last Name' : 'Last Name'}
                                        value={lastName}
                                        autocapitalize="words"
                                        required={requireLastName}
                                        onInput={setLastName}
                                    />
                                </div>
                            </div>

                            <div className={classes.readOnlyField}>
                                <TextField
                                    name="email"
                                    label="Email"
                                    value={userState.state.email || ''}
                                    onInput={() => {}}
                                />
                            </div>

                            <TextField
                                name="address"
                                label="Address"
                                value={address}
                                autocapitalize="words"
                                onInput={setAddress}
                            />

                            <PhoneField
                                method={mobilePhone}
                                methodChanged={(method) => setMobilePhone(method as IPhoneContactMethod)}
                            />

                            <PhoneField
                                method={workPhone}
                                methodChanged={(method) => setWorkPhone(method as IPhoneContactMethod)}
                            />

                            <Text type="detail">This is the name and contact info other people see. Update it here and it changes everywhere.</Text>

                            <IonButton
                                data-id="save-button"
                                expand="block"
                                disabled={!isValid}
                                onClick={onSave}
                            >
                                Save
                            </IonButton>

                            {/* Gated on isOpen (rather than always mounted like the rest of this modal's
                                content) so the personal memories fetch only fires once the user actually
                                opens Account Settings, not on every app boot -- this modal lives
                                permanently in the nav drawer tree behind isOpen=false. */}
                            {isOpen && (
                                <DrawerListSection data-id="account-settings-memories-section">
                                    <MemoryList model={memoriesFactory.forPersonal()} />
                                </DrawerListSection>
                            )}
                        </div>
                    </IonCardContent>
                </IonContent>
            </Modal>
            <EditAvatarDrawer
                isOpen={isAvatarDrawerOpen}
                animationOrigin={avatarDrawerAnimationOrigin}
                memberId="account"
                title="Edit Your Avatar"
                avatar={avatar}
                onSave={changed => { setAvatar(changed); setIsAvatarDrawerOpen(false); }}
                onCancel={() => setIsAvatarDrawerOpen(false)}
            />
        </>
    );
};
